<template>
  <div :ref="templateRef" class="ce-Item">
    <AppIcon class="ce-Icon" height="24" icon-name="calendar-check" width="24" />
    <i18n-t :keypath="displayItems.action">
      <template #userName>
        <span
          v-tippy="{
            placement: 'top',
            content: displayItems.author
          }"
          class="ce-AuthorName oboard-truncated-text"
        >
          {{ displayItems.author }}
        </span>
      </template>
    </i18n-t>
    <div class="ce-Item_ElementInfo">
      <OkrIcon :objective="displayItems.okrElement" :size="MD" class="ce-ElementInfo_OkrElement" />

      <ObjectiveId
        :value="displayItems.displayId"
        size="xs"
        style="--id-font-weight: 700"
        @click="onIdClick"
      />

      <div class="ce-Item_Name">
        {{ displayItems.title }}
      </div>
    </div>
    <span v-if="item.timing" class="ce-Item_Ago" data-testid="notification-timing">
      {{ item.timing }}
    </span>
    <AppBadge
      v-if="!item.isRead"
      class="ce-Item_Marker"
      data-testid="notification-unread-marker"
      inline
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { tracker } from '@/tracking/amplitude'
import { EVENT_NAMES, EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { SIZES } from '@/utils/components-configurations/okr-icon'
import { NOTIFICATION_ATTRIBUTES, useOkrElementClick } from '@/utils/in-app-notifications'
import { getFirstName } from '@/utils/users-helpers'

import OkrIcon from '@/components/objectives/items/OkrIcon'
import ObjectiveId from '@/components/objectives/ObjectiveId'
import AppBadge from '@/components/ui/AppBadge/AppBadge'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import {
  getNotificationElementEntity,
  useMarkAsRead
} from '@/components/ui/InAppNotifications/in-app-notifications-utils'

defineOptions({
  name: 'CheckinsEntity'
})

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})
const { MD } = SIZES

const emit = defineEmits({ 'mark-as-read': null, 'update-notifications-statuses': null })

const markAsRead = () => {
  emit('mark-as-read', props.item.id)
}

const templateRef = 'wrapperReference'

useMarkAsRead({ item: props.item, callback: markAsRead, templateRef })

const { AUTHOR_NAME, ELEMENT_TITLE, ELEMENT_DISPLAY_ID, CHECKIN_ACTION } = NOTIFICATION_ATTRIBUTES

const CHECKIN_ACTIONS = {
  MADE: 0,
  EDIT: 1
}

const displayItems = computed(() => {
  const { normalizedAttributes } = props.item
  const author = getFirstName(normalizedAttributes[AUTHOR_NAME] || '')
  const actionKey = normalizedAttributes[CHECKIN_ACTION]
  const actionMap = {
    [CHECKIN_ACTIONS.MADE]: 'checkin.user_made_checkin',
    [CHECKIN_ACTIONS.EDIT]: 'checkin.user_edit_checkin'
  }

  const action = actionMap[actionKey] || ''
  const { okrElement } = getNotificationElementEntity(normalizedAttributes)

  return {
    author,
    displayId: normalizedAttributes[ELEMENT_DISPLAY_ID],
    action,
    okrElement,
    title: normalizedAttributes[ELEMENT_TITLE]
  }
})
const { handleClick } = useOkrElementClick()

const route = useRoute()

const onIdClick = () => {
  handleClick(displayItems.value.okrElement)

  tracker.logEvent(EVENT_NAMES.NOTIFICATION_OPENED, {
    source: EVENT_SOURCES.IN_APP,
    target: route.name
  })

  emit('update-notifications-statuses')
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/_notification-center';

.ce-Item {
  @extend %notification-item-styles;
}

.ce-Item_Ago {
  flex: 0 1 auto;
  color: $dark-3;
  font-weight: fw('regular');
  min-width: fit-content;
}

.ce-Item_Marker {
  margin-left: auto;
  --backdrop-size: 24px;
}

.ce-Icon {
  min-width: 24px;
  min-height: 24px;
  display: flex;
  color: $primary-color-next;
}

.ce-Item_ElementInfo {
  font-family: $system-ui;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 16px;
  --id-font-weight: 600;
  max-width: 200px;
}
.ce-ElementInfo_OkrElement {
  margin-right: 4px;
}

.ce-Item_Name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: $fs-12;
  line-height: 16px;
  color: $dark-2;
}

.ce-AuthorName {
  font-weight: fw('semi-bold');
  max-width: 125px;
}
</style>
